import { useEffect, useState } from 'react';

const STEPS = [
  {
    id: 'you',
    label: 'You',
    short: 'Choose a tool',
    detail: 'Pick an algorithm or custom QGIS model and open it in the browser toolbox.',
  },
  {
    id: 'toolbox',
    label: 'Toolbox',
    short: 'Set parameters',
    detail: 'Configure inputs and options with a guided form — no desktop QGIS required for operators.',
  },
  {
    id: 'recipes',
    label: 'Recipes & schedules',
    short: 'Save & automate',
    detail: 'Save parameter sets as recipes for reuse, or schedule models to run on a timetable.',
  },
  {
    id: 'jobs',
    label: 'Jobs',
    short: 'Track progress',
    detail: 'Submit a run and follow status and history from the jobs list while processing continues.',
  },
  {
    id: 'qgis',
    label: 'QGIS Processing',
    short: 'Run the analysis',
    detail: 'Your QGIS installation executes the algorithm or Model Designer workflow.',
  },
  {
    id: 'results',
    label: 'Results',
    short: 'Map & reports',
    detail: 'Explore outputs on the map, review analytics, download layers, and export PDF reports.',
  },
];

const CYCLE_MS = 2400;

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  return reduced;
}

export default function HowItWorksDiagram({ animated = true }) {
  const reducedMotion = usePrefersReducedMotion();
  const live = animated && !reducedMotion;
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (!live || paused) return undefined;
    const id = window.setInterval(() => {
      setActive((i) => (i + 1) % STEPS.length);
    }, CYCLE_MS);
    return () => window.clearInterval(id);
  }, [live, paused]);

  const step = STEPS[active];
  const progress = active / (STEPS.length - 1);

  return (
    <div
      className={`how-flow${live ? ' is-animated' : ''}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setPaused(false);
      }}
    >
      <div className="how-flow-rail" aria-hidden="true">
        <div className="how-flow-rail-track" />
        <div
          className="how-flow-rail-fill"
          style={{ transform: `scaleX(${progress})` }}
        />
        <div
          className="how-flow-rail-dot"
          style={{ left: `${progress * 100}%` }}
        />
      </div>

      <ol className="how-flow-track" aria-label="How GIS Liberty works">
        {STEPS.map((item, index) => (
          <li key={item.id} className="how-flow-step-wrap">
            <button
              type="button"
              className={`how-flow-step${index === active ? ' is-active' : ''}${index < active ? ' is-done' : ''}`}
              onClick={() => setActive(index)}
              aria-current={index === active ? 'step' : undefined}
            >
              <span className="how-flow-num">{index + 1}</span>
              <span className="how-flow-label">{item.label}</span>
              <span className="how-flow-short">{item.short}</span>
            </button>
          </li>
        ))}
      </ol>

      <div className="how-flow-detail panel-card-inset" aria-live="polite">
        <p className="how-flow-detail-eyebrow">
          Step {active + 1} of {STEPS.length}
          {live ? (paused ? ' · paused' : ' · auto-playing') : ''}
        </p>
        <h3>{step.label}</h3>
        <p>{step.detail}</p>
      </div>
    </div>
  );
}
